import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UploadSimple, FloppyDisk } from "@phosphor-icons/react";

function readAsDataURL(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function ImageField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-zinc-500">{label}</Label>
      <div className="mt-2 flex items-center gap-3">
        {value ? (
          <img src={value} alt="" className="h-16 w-16 object-contain bg-[#0D0D10] border border-[#2D2D35] rounded" />
        ) : (
          <div className="h-16 w-16 bg-[#0D0D10] border border-[#2D2D35] rounded flex items-center justify-center text-zinc-600 text-[10px]">No image</div>
        )}
        <label className="flex items-center gap-2 text-xs cursor-pointer border border-[#2D2D35] hover:border-[#E50914] px-3 py-2 rounded transition-colors">
          <UploadSimple size={14} /> Upload
          <input type="file" accept="image/*" hidden data-testid={testid}
                 onChange={async (e) => {
                   const f = e.target.files?.[0]; if (!f) return;
                   const u = await readAsDataURL(f); onChange(u);
                 }} />
        </label>
        {value && <button onClick={()=>onChange(null)} className="text-xs text-zinc-500 hover:text-[#FF3B30]">remove</button>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type="text" }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input value={value ?? ""} type={type} onChange={(e) => onChange(e.target.value)} data-testid={testid}
             className="mt-2 bg-[#0D0D10] border-[#2D2D35] focus:border-[#E50914] focus:ring-1 focus:ring-[#E50914]" />
    </div>
  );
}

export default function CompanySettings() {
  const [profile, setProfile] = useState(null);
  const [slip, setSlip] = useState(null);

  useEffect(() => {
    api.get("/company-profile").then((r) => setProfile(r.data));
    api.get("/salary-slip-settings").then((r) => setSlip(r.data));
  }, []);

  async function saveProfile() {
    try { await api.put("/company-profile", profile); toast.success("Company profile saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  }
  async function saveSlip() {
    try { await api.put("/salary-slip-settings", slip); toast.success("Slip settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  if (!profile || !slip) return <div className="p-10 text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="p-6 md:p-10">
      <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">Settings</p>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-6">Company & Salary Slip</h1>

      <Tabs defaultValue="company" className="space-y-6">
        <TabsList className="bg-[#16161C] border border-[#2D2D35]">
          <TabsTrigger value="company" data-testid="tab-company">Company Profile</TabsTrigger>
          <TabsTrigger value="slip" data-testid="tab-slip">Salary Slip Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="company">
          <div className="bg-[#16161C] border border-[#2D2D35] rounded-md p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Company Name" value={profile.company_name} onChange={(v)=>setProfile({...profile, company_name: v})} testid="cp-name" />
              <Field label="Legal Name" value={profile.legal_name} onChange={(v)=>setProfile({...profile, legal_name: v})} />
              <Field label="Address" value={profile.address} onChange={(v)=>setProfile({...profile, address: v})} testid="cp-address" />
              <div className="grid grid-cols-3 gap-3">
                <Field label="State" value={profile.state} onChange={(v)=>setProfile({...profile, state: v})} />
                <Field label="Country" value={profile.country} onChange={(v)=>setProfile({...profile, country: v})} />
                <Field label="PIN" value={profile.pin_code} onChange={(v)=>setProfile({...profile, pin_code: v})} />
              </div>
              <Field label="Phone" value={profile.phone} onChange={(v)=>setProfile({...profile, phone: v})} />
              <Field label="Mobile" value={profile.mobile} onChange={(v)=>setProfile({...profile, mobile: v})} />
              <Field label="Email" value={profile.email} onChange={(v)=>setProfile({...profile, email: v})} testid="cp-email" />
              <Field label="Website" value={profile.website} onChange={(v)=>setProfile({...profile, website: v})} />
              <Field label="GST Number" value={profile.gst_number} onChange={(v)=>setProfile({...profile, gst_number: v})} testid="cp-gst" />
              <Field label="PAN Number" value={profile.pan_number} onChange={(v)=>setProfile({...profile, pan_number: v})} />
              <Field label="Registration Number" value={profile.registration_number} onChange={(v)=>setProfile({...profile, registration_number: v})} />
              <Field label="Authorized Signatory" value={profile.authorized_signatory} onChange={(v)=>setProfile({...profile, authorized_signatory: v})} />
              <Field label="Signatory Designation" value={profile.signatory_designation} onChange={(v)=>setProfile({...profile, signatory_designation: v})} />
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <ImageField label="Company Logo" value={profile.logo} onChange={(v)=>setProfile({...profile, logo: v})} testid="cp-logo-upload" />
              <ImageField label="Authorized Signature" value={profile.signature_image} onChange={(v)=>setProfile({...profile, signature_image: v})} />
              <ImageField label="Company Stamp" value={profile.stamp_image} onChange={(v)=>setProfile({...profile, stamp_image: v})} />
            </div>

            <div className="mt-6 flex justify-end">
              <Button onClick={saveProfile} className="bg-[#E50914] hover:bg-[#C80812]" data-testid="cp-save">
                <FloppyDisk size={16} className="mr-1" /> Save Company Profile
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="slip">
          <div className="bg-[#16161C] border border-[#2D2D35] rounded-md p-6 space-y-4">
            <Toggle label="Show company logo on slip" value={slip.show_logo} onChange={(v)=>setSlip({...slip, show_logo: v})} />
            <Toggle label="Show authorized signature" value={slip.show_signature} onChange={(v)=>setSlip({...slip, show_signature: v})} />
            <Toggle label="Show company stamp" value={slip.show_stamp} onChange={(v)=>setSlip({...slip, show_stamp: v})} />
            <Toggle label="Show QR verification" value={slip.show_qr} onChange={(v)=>setSlip({...slip, show_qr: v})} />
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Footer Notes</Label>
              <Textarea value={slip.footer_notes || ""} onChange={(e)=>setSlip({...slip, footer_notes: e.target.value})}
                        className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="slip-footer" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Terms & Conditions</Label>
              <Textarea value={slip.terms_conditions || ""} onChange={(e)=>setSlip({...slip, terms_conditions: e.target.value})}
                        className="mt-2 bg-[#0D0D10] border-[#2D2D35]" />
            </div>
            <div className="flex justify-end">
              <Button onClick={saveSlip} className="bg-[#E50914] hover:bg-[#C80812]" data-testid="slip-save">
                <FloppyDisk size={16} className="mr-1" /> Save Slip Settings
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Toggle({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between border-b border-[#2D2D35]/60 pb-3">
      <span className="text-sm">{label}</span>
      <Switch checked={value} onCheckedChange={onChange} className="data-[state=checked]:bg-[#E50914]" />
    </div>
  );
}
